import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Star, Clock, Truck, ChevronDown, ChevronUp } from 'lucide-react';

export default function LocalVsCorporateFlorists() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapError, setMapError] = useState(false);

  // Search URL for local florists
  const localFloristsSearchUrl = "/search?location=Sydney+NSW&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.8688&lng=151.2093&occasions=Birthday&occasions=Anniversary&occasions=Wedding&occasions=Sympathy&occasions=Get+Well&occasions=Congratulations&occasions=Thank+You&occasions=New+Baby&occasions=Valentine%27s+Day&occasions=Mother%27s+Day&occasions=Christmas&occasions=Graduation&occasions=Housewarming&occasions=Corporate&categories=Bouquet&categories=Floral+Arrangement&categories=Potted+Plant&categories=Single+Flower&categories=Other";

  useEffect(() => {
    const loadGoogleMaps = () => {
      if (window.google && window.google.maps) {
        initMap();
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = initMap;
      script.onerror = () => setMapError(true);
      document.head.appendChild(script);
    };

    const initMap = () => {
      if (!mapRef.current) return;

      try {
        const map = new window.google.maps.Map(mapRef.current, {
          center: { lat: -33.8688, lng: 151.2093 },
          zoom: 11,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        });

        // Add marker for Sydney CBD
        new window.google.maps.Marker({
          position: { lat: -33.8688, lng: 151.2093 },
          map: map,
          title: 'Sydney CBD - Local vs Corporate Florists',
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="16" cy="16" r="12" fill="brand-primary"/>
                <circle cx="16" cy="16" r="8" fill="white"/>
                <circle cx="16" cy="16" r="4" fill="brand-primary"/>
              </svg>
            `),
            scaledSize: new window.google.maps.Size(32, 32)
          }
        });
      } catch (error) {
        console.error('Error initializing map:', error);
        setMapError(true);
      }
    };

    loadGoogleMaps();
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What's the difference between local and corporate florists?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local florists offer personalized service, fresher flowers from daily market visits, and community knowledge. Corporate chains rely on centralized distribution, generic arrangements, and less personal service."
        }
      },
      {
        "@type": "Question",
        "name": "Are local florists more expensive than corporate chains?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local florists often provide better value despite similar pricing, investing more in quality flowers and craftsmanship rather than marketing and corporate overhead."
        }
      },
      {
        "@type": "Question",
        "name": "Do local florists offer same-day delivery like corporate chains?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, most local Sydney florists offer same-day delivery with better reliability since they know local areas intimately and have established delivery relationships."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Local vs Corporate Florists | Same-Day Flower Delivery | MarketBloom</title>
        <meta name="description" content="Discover why local Sydney florists consistently deliver superior quality, personalized service, and better value than corporate chains. Support your community's flower artisans." />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      
      <div className="pt-20 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-6 py-8">
            <div className="flex items-center gap-3 text-sm text-black mb-4">
              <span className="bg-[#967F81] text-white px-3 py-1 rounded-full font-medium">Buying Guides</span>
              <span>•</span>
              <span>25 January 2024</span>
              <span>•</span>
              <span>7 min read</span>
            </div>
            <h1 className="scroll-m-20 text-4xl font-extrabold tracking-tight text-black lg:text-5xl">
              Why Local Florists Beat Corporate Chains Every Time 2025
            </h1>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-6 py-12">
          
          {/* Hero Image */}
          <div className="mb-12">
            <img 
              src="/images/blog/local-vs-corporate-florists-hero.jpg" 
              alt="Side-by-side comparison of a local Sydney florist's artisan arrangement versus a corporate chain's generic bouquet"
              className="w-full h-80 object-cover rounded-lg shadow-lg"
            />
          </div>

          {/* Introduction */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <p className="text-xl leading-7 text-black mb-6">
              I think you'll agree with me when I say:
            </p>
            <p className="leading-7 text-black mb-6">
              Choosing between local Sydney florists and corporate chains shouldn't mean sacrificing quality for convenience or paying premium prices for generic arrangements that lack personal touch.
            </p>
            <p className="leading-7 text-black mb-6">
              That's why MarketBloom exclusively partners with independent Sydney florists who consistently outperform corporate chains - from the artisan studios in Surry Hills creating bespoke arrangements to the family-run shops in Parramatta offering personalized service that big chains simply cannot match.
            </p>
            <p className="leading-7 text-black">
              Whether you need flowers for a special occasion, corporate event, or just because, local florists deliver superior freshness, personalized service, community knowledge, and better value - all while supporting the talented artisans who make Sydney's flower scene so vibrant.
            </p>
          </div>

          {/* Quick Action CTA */}
          <div className="bg-[#967F81] text-white rounded-lg p-6 mb-12 text-center">
            <h2 className="text-2xl font-bold mb-3">Support Local Florists Today</h2>
            <p className="text-lg mb-4 opacity-90">Browse Sydney's finest independent florists offering superior quality and service</p>
            <Link 
              to={`${localFloristsSearchUrl}&utm_source=seo&utm_medium=landing&utm_campaign=local-vs-corporate`}
              className="bg-white text-brand-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center gap-2 min-h-[48px]"
            >
              Shop Local Florists
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {/* Freshness and Quality Differences */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Freshness and Quality: Local Wins Every Time</h2>
            
            <p className="leading-7 text-black mb-6">
              Local florists typically source their flowers directly from Sydney's wholesale markets, often visiting multiple times per week to hand-select the freshest blooms. This direct relationship with suppliers means flowers spend less time in transit and storage, arriving at your door with maximum freshness and longevity that can extend the life of your arrangement by days.
            </p>
            <p className="leading-7 text-black mb-6">
              Corporate chains, by contrast, often rely on centralized distribution systems where flowers may sit in warehouses for days before reaching local fulfillment centers. This extended supply chain inevitably impacts freshness and reduces the lifespan of your arrangements, meaning you get less value for your money and shorter-lived beauty.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-4">
                <div className="bg-[#967F81]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-brand-primary" aria-label="Icon for fresh quality" />
                </div>
                <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Daily Fresh Selection</h3>
                <p className="text-black">Hand-picked from Sydney Flower Market</p>
              </div>
              <div className="text-center p-4">
                <div className="bg-[#967F81]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-brand-primary" aria-label="Icon for longer lasting" />
                </div>
                <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Longer Lasting</h3>
                <p className="text-black">Minimal time from market to your door</p>
              </div>
              <div className="text-center p-4">
                <div className="bg-[#967F81]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-brand-primary" aria-label="Icon for local sourcing" />
                </div>
                <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Local Sourcing</h3>
                <p className="text-black">Direct relationships with suppliers</p>
              </div>
            </div>
          </div>

          {/* Service Coverage Map */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Local Knowledge vs Corporate Generic Service</h2>
            
            <p className="leading-7 text-black mb-6">
              Local florists know their communities intimately - from the best delivery routes to avoid traffic to understanding cultural preferences for different occasions. They remember your preferences, know your delivery address quirks, and can provide recommendations based on years of serving your neighborhood.
            </p>

            <div className="mb-6">
              <div 
                ref={mapRef} 
                className="w-full h-80 rounded-lg shadow-md bg-gray-200"
              >
                {mapError && (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <MapPin className="w-8 h-8 mr-2" />
                    <span>Map unavailable - Sydney NSW</span>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">CBD & City</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">Eastern Suburbs</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">Inner West</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">North Shore</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">Northern Beaches</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">Western Sydney</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">Southern Sydney</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">Hills District</span>
              </div>
            </div>
          </div>

          {/* Value and Pricing Comparison */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Better Value: Local Investment vs Corporate Overhead</h2>
            
            <p className="leading-7 text-black mb-6">
              While corporate chains may advertise low prices, local florists often provide better overall value. Without the overhead of massive marketing budgets, corporate offices, and shareholder profits, local florists can invest more in quality flowers, skilled craftsmanship, and personalized service that makes every arrangement special.
            </p>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="scroll-m-20 text-2xl font-semibold tracking-tight text-black mb-4">Where Your Money Goes:</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-black mb-3">Local Florists Invest In:</h4>
                  <ul className="space-y-2 text-black">
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-[#967F81] rounded-full"></span>
                      Premium quality flowers and materials
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-[#967F81] rounded-full"></span>
                      Skilled artisan craftsmanship
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-[#967F81] rounded-full"></span>
                      Personalized customer service
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-[#967F81] rounded-full"></span>
                      Community relationships and knowledge
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-black mb-3">Corporate Chains Spend On:</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                      Massive advertising campaigns
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                      Corporate headquarters overhead
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                      Shareholder profits and dividends
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                      Centralized distribution systems
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Why Choose MarketBloom for Local Florists */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Why MarketBloom Champions Local Over Corporate</h2>
            
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">1</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Personalized Service and Expertise</h3>
                  <p className="leading-7 text-black">Local florists take time to understand your needs, remember your preferences, and provide expert advice based on years of experience. They can suggest the perfect flowers for your specific occasion, recipient, and budget - something corporate call centers simply cannot match.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">2</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Reliability and Accountability</h3>
                  <p className="leading-7 text-black">Local florists stake their reputation on every delivery. They understand that their business depends on satisfied customers who will return and recommend them to others. This accountability drives them to ensure every arrangement meets high standards and every delivery is handled with care.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">3</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Community Investment and Support</h3>
                  <p className="leading-7 text-black">When you choose local florists, you're supporting Sydney families, creating local jobs, and keeping money in the community. These businesses sponsor local events, support community causes, and contribute to the unique character that makes each Sydney suburb special.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">4</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Creative Freedom and Customization</h3>
                  <p className="leading-7 text-black">Local florists aren't bound by corporate templates or limited to pre-designed arrangements. They can create custom designs, accommodate special requests, and adapt arrangements to perfectly match your vision - flexibility that corporate chains simply cannot offer.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">5</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Environmental and Ethical Practices</h3>
                  <p className="leading-7 text-black">Local florists often prioritize sustainable practices, source from local growers when possible, and minimize packaging waste. They're more likely to offer eco-friendly options and support ethical flower farming practices than large corporations focused primarily on profit margins.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Internal Linking Section */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Find Local Florists in Sydney Suburbs</h2>
            
            <p className="leading-7 text-black mb-6">
              Ready to experience the local difference? Discover exceptional independent florists across Sydney:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/search?location=Newtown+NSW+2042&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.8978149&lng=151.1785003&occasions=Birthday&occasions=Anniversary&occasions=Wedding&occasions=Sympathy&occasions=Get+Well&occasions=Congratulations&occasions=Thank+You&occasions=New+Baby&occasions=Valentine%27s+Day&occasions=Mother%27s+Day&occasions=Christmas&occasions=Graduation&occasions=Housewarming&occasions=Corporate&categories=Bouquet&categories=Floral+Arrangement&categories=Potted+Plant&categories=Single+Flower&categories=Other" className="text-brand-primary hover:underline">
                • Newtown local florists - creative artisan studios
              </Link>
              <Link to="/search?location=Bondi+NSW+2026&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.8915&lng=151.2767&occasions=Birthday&occasions=Anniversary&occasions=Wedding&occasions=Sympathy&occasions=Get+Well&occasions=Congratulations&occasions=Thank+You&occasions=New+Baby&occasions=Valentine%27s+Day&occasions=Mother%27s+Day&occasions=Christmas&occasions=Graduation&occasions=Housewarming&occasions=Corporate&categories=Bouquet&categories=Floral+Arrangement&categories=Potted+Plant&categories=Single+Flower&categories=Other" className="text-brand-primary hover:underline">
                • Bondi local florists - coastal community specialists
              </Link>
              <Link to="/search?location=Parramatta+NSW+2150&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.8175438&lng=151.0000257&occasions=Birthday&occasions=Anniversary&occasions=Wedding&occasions=Sympathy&occasions=Get+Well&occasions=Congratulations&occasions=Thank+You&occasions=New+Baby&occasions=Valentine%27s+Day&occasions=Mother%27s+Day&occasions=Christmas&occasions=Graduation&occasions=Housewarming&occasions=Corporate&categories=Bouquet&categories=Floral+Arrangement&categories=Potted+Plant&categories=Single+Flower&categories=Other" className="text-brand-primary hover:underline">
                • Parramatta local florists - family-run Western Sydney shops
              </Link>
              <Link to="/search?location=Surry+Hills+NSW+2010&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.8886109&lng=151.2094393&occasions=Birthday&occasions=Anniversary&occasions=Wedding&occasions=Sympathy&occasions=Get+Well&occasions=Congratulations&occasions=Thank+You&occasions=New+Baby&occasions=Valentine%27s+Day&occasions=Mother%27s+Day&occasions=Christmas&occasions=Graduation&occasions=Housewarming&occasions=Corporate&categories=Bouquet&categories=Floral+Arrangement&categories=Potted+Plant&categories=Single+Flower&categories=Other" className="text-brand-primary hover:underline">
                • Surry Hills local florists - trendy inner-city artisans
              </Link>
              <Link to="/search?location=Marrickville+NSW+2204&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.9113&lng=151.1553&occasions=Birthday&occasions=Anniversary&occasions=Wedding&occasions=Sympathy&occasions=Get+Well&occasions=Congratulations&occasions=Thank+You&occasions=New+Baby&occasions=Valentine%27s+Day&occasions=Mother%27s+Day&occasions=Christmas&occasions=Graduation&occasions=Housewarming&occasions=Corporate&categories=Bouquet&categories=Floral+Arrangement&categories=Potted+Plant&categories=Single+Flower&categories=Other" className="text-brand-primary hover:underline">
                • Marrickville local florists - multicultural community experts
              </Link>
            </div>
          </div>

          {/* Testimonial */}
          <div className="bg-neutral-50 rounded-lg px-6 py-5 mb-8 shadow-sm">
            <blockquote className="italic text-black">
              <p className="leading-7 mb-2">
                "I switched from a corporate chain to a local florist and the difference is incredible. They remember my preferences, the flowers last twice as long, and the personal service makes every order special."
              </p>
              <footer className="text-sm font-medium not-italic">
                – Michelle T., Sydney
              </footer>
            </blockquote>
          </div>

          {/* FAQ Section */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Frequently Asked Questions</h2>
            
            <div className="space-y-4">
              <div className="border-b pb-4">
                <button
                  onClick={() => setOpenFaq(openFaq === 0 ? null : 0)}
                  className="flex justify-between items-center w-full text-left p-2 hover:bg-gray-50 rounded transition-colors min-h-[48px]"
                  aria-expanded={openFaq === 0}
                  aria-controls="faq-answer-0"
                >
                  <h3 className="text-xl font-semibold text-black">What's the difference between local and corporate florists?</h3>
                  {openFaq === 0 ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                <div id="faq-answer-0" className={openFaq === 0 ? 'block' : 'hidden'}>
                  <p className="mt-3 leading-7 text-black px-2">
                    Local florists offer personalized service, fresher flowers from daily market visits, and community knowledge. Corporate chains rely on centralized distribution, generic arrangements, and less personal service.
                  </p>
                </div>
              </div>

              <div className="border-b pb-4">
                <button
                  onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
                  className="flex justify-between items-center w-full text-left p-2 hover:bg-gray-50 rounded transition-colors min-h-[48px]"
                  aria-expanded={openFaq === 1}
                  aria-controls="faq-answer-1"
                >
                  <h3 className="text-xl font-semibold text-black">Are local florists more expensive than corporate chains?</h3>
                  {openFaq === 1 ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                <div id="faq-answer-1" className={openFaq === 1 ? 'block' : 'hidden'}>
                  <p className="mt-3 leading-7 text-black px-2">
                    Local florists often provide better value despite similar pricing, investing more in quality flowers and craftsmanship rather than marketing and corporate overhead.
                  </p>
                </div>
              </div>

              <div className="border-b pb-4">
                <button
                  onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
                  className="flex justify-between items-center w-full text-left p-2 hover:bg-gray-50 rounded transition-colors min-h-[48px]"
                  aria-expanded={openFaq === 2}
                  aria-controls="faq-answer-2"
                >
                  <h3 className="text-xl font-semibold text-black">Do local florists offer same-day delivery like corporate chains?</h3>
                  {openFaq === 2 ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                <div id="faq-answer-2" className={openFaq === 2 ? 'block' : 'hidden'}>
                  <p className="mt-3 leading-7 text-black px-2">
                    Yes, most local Sydney florists offer same-day delivery with better reliability since they know local areas intimately and have established delivery relationships.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Final CTA */}
          <div className="bg-gradient-to-r from-[brand-primary] to-[#E63E1C] text-white rounded-lg p-8 text-center md:relative sticky bottom-0 z-50">
            <h2 className="scroll-m-20 text-3xl font-semibold tracking-tight text-white mb-4">Ready to Support Local Florists?</h2>
            <p className="text-xl leading-7 text-white mb-6 opacity-90">
              Experience the superior quality and service of Sydney's independent florists
            </p>
            <Link 
              to={`${localFloristsSearchUrl}&utm_source=seo&utm_medium=landing&utm_campaign=local-vs-corporate`}
              className="bg-white text-brand-primary px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors inline-flex items-center gap-3 text-lg min-h-[48px]"
            >
              Shop Local Florists Now
              <ArrowRight className="w-6 h-6" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
} 